<template>
  <div class="opc-cart__next">
    <slot v-if="totalPanelList.length" name="header" />
    <template v-if="!showPendingPlanSummary">
      <div v-for="totalItem in totalPanelList" :key="totalItem.billingCycle" class="opc-cart__next__item">
        <h3 class="opc-cart__next__title" :class="[(isInrCitEligible || $context.is.delta) ? 'opc-cart__next__title__preview' : '']">
          <span v-if="totalItem.labelInfo">
            {{ totalItem.labelInfo.label }}
            <vloader v-if="totalItem.labelInfo.showLoader" :loading="totalItem.labelInfo.loading" :value="totalItem.labelInfo.date" />
          </span>
          <template v-else>{{ totalItem.label }}</template>
          <zm-tooltip
            placement="top"
            role="button"
            :aria-label="`Learn more about ${totalItem.label}`"
            v-if="showTrialTip(totalItem.billingCycle) && trialTipContent"
            :content="trialTipContent">
            <i class="zm-icon-info-outline"/>
          </zm-tooltip>
          <div class="opc-cart__next__value text-no-capitalize" :class="[(isInrCitEligible || $context.is.delta) ? 'opc-cart__next__value__preview' : '']">
            <vloader :loading="chargeAmountLoading" :value="$money(totalItem.amount)" />
          </div>
        </h3>
        <next-charge-date :billing-cycle='totalItem.billingCycle' :loading='mixLoading' v-if="showNextChargeDate">
          <template v-if='showFreeTrialDate(totalItem.billingCycle)' #default>
            {{ freeTrialChargeDate }}
          </template>
        </next-charge-date>
      </div>
    </template>
    <pending-plan-summary v-else :loading="chargeAmountLoading" />
  </div>
</template>

<script>
import moment from 'moment';
import { mapGetters, mapMutations } from 'vuex';
import { ALLOW_MIXED_BUY_FREE_TRIAL_PLANS, BILLING_CYCLE, DEFAULT_FREE_TRIAL_DAYS } from '@/common/constants';
import { plansof } from '@/utils/chain';
import { debounce } from 'lodash';
import pageFlow from '@/flows';
import nextMixin from '@/views/config/shopping-cart/nextMixin';
import NextChargeDate from '@/views/config/shopping-cart/next-charge-date.vue';
import vloader from '@/views/config/shopping-cart/prorated/vloader.vue';
import { PREVIEW_API_ACTIONS_ENUM } from '@/store/modules/preview';
import { dateTimeFormat, LocalDateFormat } from '@/utils/dateformat';
import PendingPlanSummary from '@/views/config/shopping-cart/pending-plan-summary.vue';

export const span = {
  [BILLING_CYCLE.MONTHLY]: [1, 'month'],
  [BILLING_CYCLE.ANNUAL]: [1, 'year'],
  [BILLING_CYCLE.QUARTERLY]: [1, 'quarter'],
  [BILLING_CYCLE.TWO_YEARS]: [2, 'year'],
  [BILLING_CYCLE.THREE_YEARS]: [3, 'year'],
};

export default {
  components: { NextChargeDate, vloader, PendingPlanSummary },
  mixins: [nextMixin],
  props: {
    extLoading: {
      default: null
    },
    disableTriggerPreview: {
      default: false
    },
    showPendingPlanSummary: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: false,
    }
  },
  mounted() {
    if (this.needCallNextChargeDateApi) {
      this.mx_enableServerNextChargeDateLogic && this.loadNextChargeDate();
    }
  },
  computed: {
    ...mapGetters([
      'getSelectedPlans',
      'nextBillAmount',
      'getCycledTaxTotalAmount',
      'isInrCitEligible',
      'isZuoraOutage',
      'getNextCycledTaxTotalAmount',
      'isLoadingTax',
      'cartFingerPrint',
      'nextChargeDate',
      'couponFingerPrint',
      'computedAmountLoading',
      'isSelectedSameAsEditablePlansInBought',
    ]),
    showNextChargeDate() {
      return !this.isProAnnualBilledMonthlyToMonthlyDowngrade && !this.isProMonthlyToAnnualBilledMonthlyUpgrade && !this.isProAnnualBilledMonthlyDecrease;
    },
    chargeAmountLoading() {
      return this.isLoadingTax || this.computedAmountLoading;
    },
    needCallNextChargeDateApi() {
      return true;
    },
    mixLoading(){
      return this.extLoading ?? this.loading
    },
    indicator() {
      return `${this.cartFingerPrint}&${this.mx_enableServerNextChargeDateLogic}&${this.couponFingerPrint}`;
    },
    totalPanelList() {
      const totalPanelBaseOnBillingCycle = [
        {
          billingCycle: BILLING_CYCLE.MONTHLY,
          amount: this.monthlyAmount,
          label: this.monthlyLabel,
        },
        {
          billingCycle: BILLING_CYCLE.ANNUAL_TERM_MONTHLY,
          amount: this.annualTermMonthlyAmount,
          labelInfo: this.annualTermMonthlyLabelInfo,
        },
        {
          billingCycle: BILLING_CYCLE.ANNUAL,
          amount: this.annuallyAmount,
          label: this.annuallyLabel,
        },
        {
          billingCycle: BILLING_CYCLE.QUARTERLY,
          amount: this.quarterlyAmount,
          label: this.quarterlyLabel,
        },
        {
          billingCycle: BILLING_CYCLE.TWO_YEARS,
          amount: this.twoYearsAmount,
          label: this.twoYearsLabel,
        },
        {
          billingCycle: BILLING_CYCLE.THREE_YEARS,
          amount: this.threeYearsAmount,
          label: this.threeYearsLabel,
        },
      ]
      return totalPanelBaseOnBillingCycle.filter(({ amount }) => amount > 0)
    },
    /**
     * Today’s total is, as a matter of fact, not a precise amount for charing the users.
     * e.g. The user’s subscription of $15.99/monthly begins on 2024/05/10,
     * if he adds 2 licenses on 2024/05/14,
     * then the final charge amount will be approximately
     * 15.99 * 2 * (30 - (14-10)) / 30.
     * Thats why, as I mentioned above, the today’s total has been through multiple changes
     * and finally updated to New Monthly Total to avoid legal issues.
     * (we are going to implement a preview API call from zuora in the next half of this year
     * which is expected to provide an accurate amount. I think, by then, it will be okay to
     * display today’s total with the exact amount).
     * -- Henry Yu 2024/05/14
     */
    monthlyAmount() {
      return this.nextBillAmount?.monthlyCharge + (this.getNextCycledTaxTotalAmount[BILLING_CYCLE.MONTHLY]??0);
    },
    freeTrialChargeDate() {
      return this.nextAfterTrialChargeDate(new Date(), this.selectedFreeTrialPlan?.trialDays ?? DEFAULT_FREE_TRIAL_DAYS);
    },
    annuallyAmount() {
      return this.nextBillAmount?.annualCharge + (this.getNextCycledTaxTotalAmount[BILLING_CYCLE.ANNUAL]??0);
    },
    annualTermMonthlyAmount() {
      return this.nextBillAmount?.annualTermMonthlyCharge + (this.getNextCycledTaxTotalAmount[BILLING_CYCLE.ANNUAL_TERM_MONTHLY]??0);
    },
    quarterlyAmount() {
      return this.nextBillAmount?.quarterlyCharge + (this.getNextCycledTaxTotalAmount[BILLING_CYCLE.QUARTERLY]??0);
    },
    twoYearsAmount() {
      return this.nextBillAmount?.twoYearsCharge + (this.getNextCycledTaxTotalAmount[BILLING_CYCLE.TWO_YEARS]??0);
    },
    threeYearsAmount() {
      return this.nextBillAmount?.threeYearsCharge + (this.getNextCycledTaxTotalAmount[BILLING_CYCLE.THREE_YEARS]??0);
    },
    // BUY TRIAL
    // FREE TRIAL - next date changed to startday + trial days
    isFreeTrialFlow () {
      return this.$context.is.add2trial || this.$context.is.buyTrial;
    },
    isPureFreeTrialFlow() {
      return this.isFreeTrialFlow && !plansof(this.getSelectedPlans).not().freeTrial().has() ;
    },
    isMixedFreeTrialFlow() {
      return this.isFreeTrialFlow &&
        plansof(this.getSelectedPlans).type(...ALLOW_MIXED_BUY_FREE_TRIAL_PLANS).has() &&
        plansof(this.getSelectedPlans).not().freeTrial().has()
    },
    hasFreeTrialPlan() {
      return plansof(this.getSelectedPlans).freeTrial().has();
    },
    selectedFreeTrialPlan() {
      if (this.hasFreeTrialPlan) {
        return plansof(this.getSelectedPlans).freeTrial().get(0);
      }
      return null;
    },
    trialTipContent() {
      const trialPlan = plansof(this.getSelectedPlans).freeTrial().get(0)
      if (trialPlan) {
        const trialDays = trialPlan.trialDays || DEFAULT_FREE_TRIAL_DAYS
        const billingCycle = trialPlan.billing_cycle || BILLING_CYCLE.MONTHLY
        const planName = this.$t(trialPlan.typeNameKey)

        const startDate = moment()
        const endDate = dateTimeFormat(this.freeTrialEndDate(startDate, trialDays))
        return this.$t('billing.order_summary.total_billed_trial_tip', {
          date: endDate,
          plan: planName,
          billingCycle: this.$t(`billing.cycle.${billingCycle}`)?.toLowerCase()
        })
      } else {
        return ''
      }
    }
  },
  methods: {
    ...mapMutations(['setNextChargeDate']),
    // FREE TRIAL - after trial days
    nextAfterTrialChargeDate(startTime, trialDays = DEFAULT_FREE_TRIAL_DAYS) {
      const time = moment(startTime)
        .add(trialDays, 'days')
        .format(LocalDateFormat);
      return this.isZuoraOutage ? this.$t('billing.zuoraoutage.expire_date', [time]): `${this.$t('billing.order_summary.next_charge_date')} ${time}`;
    },
    loadNextChargeDate: debounce(async function() {
      if (this.isSelectedSameAsEditablePlansInBought) return;
      if (this.disableTriggerPreview) return;
      this.loading = true;
      try {
        const { eventResult = {} } = (await pageFlow.submit({
          preview: true,
          previewActions: [PREVIEW_API_ACTIONS_ENUM.NEXT_CHARGE_DATE],
        })) || {};
        if (eventResult?.nextChargeDate) {
          const nextChargeDate = JSON.parse(eventResult.nextChargeDate);
          this.setNextChargeDate(nextChargeDate);
        }
      } finally {
        this.loading = false;
      }
    }, 1000),
    showTrialTip(billingCycle) {
      const trialPlan = plansof(this.getSelectedPlans).freeTrial().get(0)
      if (trialPlan && (this.$context.is.buyNow || this.$context.is.basic2pro || this.isMixedFreeTrialFlow)) {
        return trialPlan.billing_cycle === billingCycle
      }
    },
    freeTrialEndDate(startTime = moment(), trialDays = DEFAULT_FREE_TRIAL_DAYS) {
      // TODO use server time
      return moment(startTime).add(trialDays, 'days')
    },
    showFreeTrialDate(billingCycle) {
      //backend support compute add2trial's next charge date
      if (this.$context.is.add2trial && this.mx_enableServerNextChargeDateLogic) {
        return false
      } else if (this.isPureFreeTrialFlow) {
        return true
      } else if (this.hasFreeTrialPlan) {
        return !plansof(this.getSelectedPlans).not().freeTrial().yes().cycle(billingCycle).has()
      } else {
        return false
      }
    },
  },
  watch: {
    indicator: {
      async handler() {
        if (this.needCallNextChargeDateApi) {
          this.mx_enableServerNextChargeDateLogic && await this.loadNextChargeDate()
        }
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import "next-scope";
.zm-icon-info-outline {
  margin-left: 5px;
  color: #515079
}
</style>

<style lang="scss">
@import "next";
</style>
